import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
    ColorNode, styles,
    OrgToThemeMap,
} from 'src/organization-styles';
import { ApolloService, getUser_users_by_pk_Organization } from 'src/gql-generated/generated';
import { CookieService } from './cookie.service';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationService {
    /**
     * URL of logo for organization user is part of
     */
    logoURL?: string;

    /**
     * Current organization settings are loaded for
     * Can be subscribed to to get notified of organization update
     */
    currentOrganization$ = new BehaviorSubject<(Omit<getUser_users_by_pk_Organization, 'settings'> &
        { settings?: OrganizationSettings; }) | undefined>(undefined);

    constructor(private api: ApiService, private apollo: ApolloService, private cookie: CookieService) { }

    /**
     * Set organization to load settings for and update UI/UX with
     * @param organization Organization to switch to
     * @param userId User to switch organization for
     */
    async setOrganization(organization: getUser_users_by_pk_Organization, userId?: string): Promise<void> {
        // Update user organization if needed
        if (userId) {
            await firstValueFrom(this.apollo.updateUser({
                id: userId,
                _set: {
                    organization_id: organization.id,
                },
            }));
        }

        // If language has changed, user will reload the app.
        const reloading = this.applyLanguage(organization);

        if (!reloading) {
            // Apply smooth color animation for organization change (not on first load)
            if (this.currentOrganization$.value) {
                this.transitionAllColors(750);
            }
            // Apply settings
            this.applyOrganizationSettings(organization);
            this.currentOrganization$.next(organization);
        }
    }

    /**
     * Determines if user should refresh the page to apply language change and store language in cookie
     * @param organization New organization user is active in
     * @returns True if user is refreshing page to apply changes, false otherwise
     */
    applyLanguage(organization: getUser_users_by_pk_Organization): boolean {
        // Get current language from cookie (Default application language is nl)
        const currentLanguage = this.cookie.getCookie('language') || 'nl';
        // Each language has a compiled application. Nginx uses language cookie to serve correct files.
        // If new organization has a different language, update the cookie and hard refresh the page.
        if (currentLanguage !== organization.language) {
            this.cookie.setCookie('language', organization.language, 365);
            setTimeout(() => {
                window.location.reload();
            });
            return true;
        }
        return false;
    }

    /**
     * Apply organization settings such as styling and enable/disable features
     * @param organization Organization to apply settings of
     */
    async applyOrganizationSettings(organization: getUser_users_by_pk_Organization): Promise<void> {
        // Update site title
        document.title = organization.app_name || `${organization.name} Chat`;
        // Apply colors
        this.applyColors(organization);
        // Load favicon
        if (organization.Favicon?.id) {
            const favicon = await this.loadImageFromStorage(organization.Favicon.id);
            if (favicon) {
                this.changeFavicon(favicon);
            }
        }
        // Load logo
        if (organization.Logo.id) {
            const logo = await this.loadImageFromStorage(organization.Logo.id);
            if (logo) {
                this.logoURL = logo;
                if (!organization.Favicon?.id) {
                    this.changeFavicon(logo);
                }
            }
        }
    }

    /**
     * Loads an image file from backend and return it as Base64 data url
     * @param id file_id of the file to retrieve
     * @returns Base64 image string or null if it's invalid or not found
     */
    private async loadImageFromStorage(id: string): Promise<string | null> {
        // Retrieve file from backend
        const response = await this.api.retrieveFile(id);

        if (response.data.type.startsWith('image/')) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                // Resolve when the full file has been read
                reader.onload = (result): void => resolve(result.target?.result as string);
                // Start reading the file
                reader.readAsDataURL(response.data);
            });
        }
        return null;
    }

    /**
     * Apply organization colors to the UI
     * @param organization Organization to apply colors of
     */
    applyColors(organization: getUser_users_by_pk_Organization): void {
        // Convert organization name to key used in colors - lowercase, remove trailing spaces and replace whitespace with _
        const orgKey = organization.name.toLowerCase().trim().replaceAll(' ', '_');
        const themeName = OrgToThemeMap[orgKey] || 'default';

        let colorMapping = this.getOrganizationColors(styles, themeName);

        // If colorMapping is empty, fall back to default
        if (Object.keys(colorMapping).length === 0) {
            colorMapping = this.getOrganizationColors(styles, 'default');
        }

        for (const [key, value] of Object.entries(colorMapping)) {
            document.documentElement.style.setProperty(key, value);
        }
    }

    /**
     * Uses the colors defined in /src/organization-styles.ts to build a color palette for the current organization
     * @param node Current node of colors to examine
     * @param organizationName Organization name (system name: lower case and spaces replaced with _)
     * @param path Current path to be considered
     * @returns Object containing all color properties for the given organization
     */
    private getOrganizationColors(node: ColorNode, organizationName: keyof typeof OrgToThemeMap, path: string[] = []): { [key: string]: string; } {
        // Final result
        const result: { [key: string]: string; } = {};

        // Loop over key-value pairs of colors
        for (const [key, value] of Object.entries(node)) {
            // Object contains subobjects, recursively add these
            if (typeof value === 'object') {
                Object.assign(
                    result,
                    this.getOrganizationColors(value as ColorNode, organizationName, [...path, key]),
                );
            }
            // Add value from organization if found
            if (key === organizationName && typeof value === 'string') {
                result[`--app-${path.join('-')}`] = value;
            }
        }
        return result;
    }

    /**
     * Change favicon
     * @param url Favicon URL
     */
    changeFavicon(url: string): void {
        let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");

        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }

        link.type = 'image/x-icon';
        link.href = url;
    }

    /**
     * Temporarily enable color transition on all elements
     * @param duration Duration in ms
     */
    transitionAllColors(duration: number): void {
        // Create a style tag with the desired styles
        const style = document.createElement('style');
        style.textContent = `
                * {
                    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
                    transition-duration: ${duration}ms;
                }
                `;
        document.head.appendChild(style);
        // Remove it after duration
        setTimeout(() => {
            document.head.removeChild(style);
        }, duration);
    }
}

/**
 * Definition of allowed organization settings
 */
export interface OrganizationSettings {
    colors: { [key: string]: string; };
    /** Available feature flags for an organization */
    features: (
        'CollectionAdvancedSettings' |
        'ChunkRetrievalButton' |
        'BaseModelChat'
    )[];
}
